import { WebHaptics, type HapticInput } from "web-haptics";
import { isTouchDevice } from "@/utils/is-touch-device";

/**
 * Haptic feedback patterns for mobile touch interactions.
 *
 * - light:     discrete state change (switch toggle, drag pickup)
 * - medium:    meaningful action completed (agent done)
 * - confirm:   clipboard copy, long-press activated
 * - warning:   warning toast
 * - error:     error toast
 */
type HapticPattern = "light" | "medium" | "confirm" | "warning" | "error";

// Maps our pattern names to web-haptics built-in presets.
// Available presets: success, warning, error, light, medium, heavy, soft, rigid, selection, nudge, buzz
const patternMap: Record<HapticPattern, HapticInput> = {
    light: "light",
    medium: "medium",
    confirm: "success",
    warning: "warning",
    error: "error",
};

// Lazy singleton — created on first use so module import has no side effects.
let instance: WebHaptics | null = null;
function getInstance(): WebHaptics {
    if (!instance) {
        instance = new WebHaptics();
    }
    return instance;
}

/**
 * Trigger haptic feedback on touch devices. No-ops silently on desktop
 * or browsers that don't support haptic feedback.
 *
 * Uses web-haptics which handles both Android (Vibration API) and
 * iOS Safari (hidden switch-checkbox trick).
 */
export function haptic(pattern: HapticPattern): void {
    if (!isTouchDevice()) return;
    void getInstance().trigger(patternMap[pattern]);
}
